/**
 * pages/BookingPage.tsx — CLI-452
 * Самозапись пациента на приём или сдачу анализов.
 * Форма: дата + время + кабинет + тип + причина.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { bookAppointment } from '../services/patient.service';

export function BookingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    roomId: '',
    date: '',
    time: '',
    reason: '',
    type: 'consultation' as 'consultation' | 'lab',
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: bookAppointment,
    onSuccess: () => navigate('/portal/appointments'),
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Ошибка при создании записи. Попробуйте позже.');
    },
  });

  const today = new Date().toISOString().split('T')[0]!;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.roomId.trim() || !form.date || !form.time) {
      setError('Укажите кабинет, дату и время');
      return;
    }
    mutation.mutate(form);
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">←</button>
        <h2 className="text-lg font-bold text-gray-800">Записаться</h2>
      </div>

      <form onSubmit={(e) => { handleSubmit(e); }} className="space-y-4">
        {/* Тип записи */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Тип записи</label>
          <div className="flex rounded-xl overflow-hidden border border-gray-200">
            {(['consultation', 'lab'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm((f) => ({ ...f, type: t }))}
                className={`flex-1 py-2.5 text-sm font-medium transition ${
                  form.type === t ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t === 'consultation' ? '👨‍⚕️ Приём' : '🧪 Анализы'}
              </button>
            ))}
          </div>
        </div>

        {/* ID кабинета */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ID кабинета</label>
          <input
            type="text"
            value={form.roomId}
            onChange={(e) => setForm((f) => ({ ...f, roomId: e.target.value }))}
            placeholder="UUID кабинета"
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-400 mt-1">Уточните ID кабинета у регистратора</p>
        </div>

        {/* Дата */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Дата</label>
          <input
            type="date"
            min={today}
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Время */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Время</label>
          <input
            type="time"
            value={form.time}
            onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Причина */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Причина обращения <span className="text-gray-400">(необязательно)</span>
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            placeholder="Опишите жалобы или укажите цель визита"
            rows={3}
            maxLength={300}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition"
        >
          {mutation.isPending ? 'Записываемся…' : 'Записаться'}
        </button>
      </form>
    </div>
  );
}
